import { NSARouterMakerResult } from "namirasoft-account-react";
import { IStorageLocal } from "namirasoft-core";
import { JournalEntryLineRow, JournalEntryMode, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, FloatFormatter, IDFormatter, IntegerFormatter, NSColumn, NSTable, NSTitle, TableColumnInfo, TableRowInfo } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpRouterProps } from "../ErpRouterProps";

const TOTAL_ROW_ID = "__total__";

interface JournalEntryLinesViewProps extends NSARouterMakerResult, ErpRouterProps
{
    journal_entry_id: string;
    currency_id: string;
    rows: JournalEntryLineRow[];
    /** Mode of the owning journal entry; drives which lines are shown and how they are labelled. */
    mode?: JournalEntryMode;
    title?: string;
}

interface JournalEntryLinesViewState
{
    currencySymbols: { [id: string]: string };
    columns: TableColumnInfo[] | null;
}

export class JournalEntryLinesView extends Component<JournalEntryLinesViewProps, JournalEntryLinesViewState>
{
    override state: JournalEntryLinesViewState = { currencySymbols: {}, columns: null };

    private Table_Ref = createRef<NSTable<JournalEntryLineRow>>();

    override componentDidMount(): void { this.loadCurrencies(); }

    override componentDidUpdate(prev: JournalEntryLinesViewProps): void
    {
        let currencyChanged = prev.currency_id !== this.props.currency_id;
        let rowsChanged = prev.rows !== this.props.rows;
        if (currencyChanged || rowsChanged)
            this.loadCurrencies();
        if (prev.journal_entry_id !== this.props.journal_entry_id || prev.mode !== this.props.mode)
            this.Table_Ref.current?.reload(null, null);
    }

    private getMode(): JournalEntryMode
    {
        return this.props.mode ?? JournalEntryMode.Normal;
    }

    /**
    * An edit request stores the reversed copy of the original lines next to the requested lines;
    * only the requested ones are shown. A delete request is made of reversed lines only and shows
    * all of them. Every other mode shows every line.
    */
    private getVisibleLines(): JournalEntryLineRow[]
    {
        let all = [...(this.props.rows ?? [])];
        if (this.getMode() === JournalEntryMode.RequestToEdit)
            return all.filter(l => !l.reversed);
        return all;
    }

    private getLineCurrencyId(row: JournalEntryLineRow): string
    {
        let lineCurrency = (row as any)?.currency_id;
        return lineCurrency || this.props.currency_id;
    }

    private rebuildColumns(): void
    {
        let columns = this.buildColumns();
        this.setState({ columns }, () =>
        {
            this.Table_Ref.current?.setColumns(columns);
            this.Table_Ref.current?.reload(null, null);
        });
    }

    private async loadCurrencies(): Promise<void>
    {
        let { servers, currency_id: entryCurrencyId, rows } = this.props;
        let ids = new Set<string>();
        if (entryCurrencyId) ids.add(entryCurrencyId);
        for (let r of rows ?? [])
        {
            let cid = (r as any)?.currency_id;
            if (cid) ids.add(cid);
        }
        try
        {
            let symbols: { [id: string]: string } = {};
            await Promise.all([...ids].map(async (id) =>
            {
                let currency = await servers.erp.currency.Get(id);
                symbols[id] = currency?.symbol ?? "";
            }));
            this.setState({ currencySymbols: symbols }, () => this.rebuildColumns());
        }
        catch (e) { console.error(e); }
    }

    private buildColumns(): TableColumnInfo[]
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let cols = db.tables.journal_entry_line.columns;
        let columns: TableColumnInfo[] = [
            BaseColumnFormatter.getColumn(cols.number, new IntegerFormatter()),
            BaseColumnFormatter.getColumn(cols.account_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(cols.party_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(cols.tag_ids, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(cols.debit, new FloatFormatter("160px")),
            BaseColumnFormatter.getColumn(cols.credit, new FloatFormatter("160px")),
            BaseColumnFormatter.getColumn(cols.currency_id, new IDFormatter(namirasoft, new IStorageLocal())),
            BaseColumnFormatter.getColumn(cols.rate_num, new FloatFormatter("100px", 2)),
            BaseColumnFormatter.getColumn(cols.rate_den, new FloatFormatter("100px", 2)),
        ];
        return columns;
    }

    private getLines = async (): Promise<{ rows: JournalEntryLineRow[], count: number }> =>
    {
        let lines = this.getVisibleLines().sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));

        let totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
        let totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);

        let totalRow = {
            id: TOTAL_ROW_ID,
            journal_entry_id: "",
            number: 0,
            account_id: "",
            party_id: null,
            tag_ids: [],
            debit: totalDebit,
            credit: totalCredit,
            created_at: new Date(),
            updated_at: new Date(),
        } as unknown as JournalEntryLineRow;

        return { rows: [...lines, totalRow], count: lines.length + 1 };
    };

    private formatMoney(value: any, currencyId: string): string
    {
        let n = Number(value) || 0;
        let formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        let symbol = (this.state.currencySymbols[currencyId] ?? "").trim();
        return symbol ? `${symbol} ${formatted}` : formatted;
    }

    private getCellFormattedValue = (value: any, column: TableColumnInfo, row: TableRowInfo<JournalEntryLineRow>, printable: boolean): any =>
    {
        if (row.value.id === TOTAL_ROW_ID)
        {
            if (column.name === "party_id") return "Total:";
            if (column.name === "debit" || column.name === "credit")
            {
                let n = Number(value) || 0;
                return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            return "";
        }
        if (column.name === "number")
            return column.formatter.format(row.index + 1, column, row, printable);
        if (column.name === "debit" || column.name === "credit")
        {
            let formatted = this.formatMoney(value, this.getLineCurrencyId(row.value));
            let isZero = !value || Number(value) === 0;
            return isZero ? <span style={{ color: "#bdbdbd" }}>{formatted}</span> : formatted;
        }
        if (column.name === "rate_num" || column.name === "rate_den")
        {
            let formatted = column.formatter.format(value, column, row, printable);
            let bothOne = Number((row.value as any).rate_num) === 1
                && Number((row.value as any).rate_den) === 1;
            return bothOne ? <span style={{ color: "#bdbdbd" }}>{formatted}</span> : formatted;
        }
        return column.formatter.format(value, column, row, printable);
    };

    private getRowAttributes = (row_value: JournalEntryLineRow, _row_index: number): { [key: string]: any } =>
    {
        if (row_value.id === TOTAL_ROW_ID)
        {
            let color = "hsla(234, 64%, 22%, 1)";
            return { style: { fontWeight: "bold", color, borderTop: "2px solid #141B5C", background: "#fff" } };
        }
        if (row_value.reversed)
            return { title: "Reversed copy of an original line", style: { background: "#ffebee" } };
        return {};
    };

    private getTitle(): string
    {
        if (this.props.title)
            return this.props.title;
        switch (this.getMode())
        {
            case JournalEntryMode.RequestToEdit: return "Requested Lines";
            case JournalEntryMode.RequestToDelete: return "Lines To Reverse (Request To Delete)";
            default: return "Lines";
        }
    }

    private getNote(): string | null
    {
        switch (this.getMode())
        {
            case JournalEntryMode.RequestToAdd:
                return "This journal entry is a pending request; it only affects the books once it is approved.";
            case JournalEntryMode.RequestToEdit:
                return "Only the requested lines are shown. The server keeps a reversed copy of the original lines inside this request so reports that include requests show the net change.";
            case JournalEntryMode.RequestToDelete:
                return "Every line of the original journal entry is shown with debit and credit swapped. Approving this request deletes the original journal entry.";
            default:
                return null;
        }
    }

    override render()
    {
        let { columns } = this.state;
        let note = this.getNote();

        return (
            <NSColumn style={{ width: "100%", marginTop: 16, gap: 8 }}>
                <NSTitle title={this.getTitle()} />
                {note && <div style={{ fontSize: 12, color: "#666" }}>{note}</div>}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", flexGrow: 1 }}>
                    <NSTable<JournalEntryLineRow>
                        ref={this.Table_Ref}
                        name="journal-entry-lines"
                        checkbox={false}
                        columns={columns ?? this.buildColumns()}
                        getRows={this.getLines}
                        getRowKey={(row) => row.value.id}
                        getCellFormattedValue={this.getCellFormattedValue}
                        getRowAttributes={this.getRowAttributes}
                        ui={{ pagination: false, button_panel: false }}
                    />
                </div>
            </NSColumn>
        );
    }
}